/**
 * ジャーナルビュー - AI質問 + チャット + 日次サマリー
 */

import { Bot, MessageSquare, NotebookPen, Plus, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { getInterviewQuestions } from '@/api/services/journal'
import { getTimelineNotes } from '@/api/services/overland'
import type { ReadHistory } from '@/api/type'
import type { InterviewQuestion } from '@/api/types/journal'
import type { TimelineNote } from '@/api/types/overland'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatMinutesJa } from '@/lib/time-format'

import { NoteInputSheet } from './activity-card/note-input-sheet'
import { ReadCard } from './activity-card/read-card'
import { TimelineNoteCard } from './activity-card/timeline-note-card'
import { ChatView } from './journal/chat-view'
import { DailySummary } from './journal/daily-summary'
import { QuestionCard } from './journal/question-card'

type Props = {
  history: ReadHistory[]
  isLoading: boolean
  selectedDate: Date
  selectedDateStr: string
}

// 日付をキーに変換
const getDateKey = (date: Date) => date.toISOString().split('T')[0]

// 読書時間を計算
const calculateTotalMinutes = (items: ReadHistory[]) =>
  items.reduce((sum, item) => {
    const start = new Date(item.started_at).getTime()
    const end = new Date(item.ended_at).getTime()
    return sum + (end - start) / 60000
  }, 0)

export const JournalView = ({ history, isLoading, selectedDate, selectedDateStr }: Props) => {
  const [questions, setQuestions] = useState<InterviewQuestion[]>([])
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
  const [activeChat, setActiveChat] = useState<InterviewQuestion | null>(null)
  const [notes, setNotes] = useState<TimelineNote[]>([])
  const [isLoadingNotes, setIsLoadingNotes] = useState(false)
  const [noteSheetOpen, setNoteSheetOpen] = useState(false)

  // 日付ごとにグループ化
  const currentDateKey = getDateKey(selectedDate)
  const currentHistory = history.filter((item) => getDateKey(new Date(item.started_at)) === currentDateKey)
  const totalMinutes = calculateTotalMinutes(currentHistory)

  // AI質問を取得
  const fetchQuestions = useCallback(async () => {
    setIsLoadingQuestions(true)
    try {
      const response = await getInterviewQuestions(selectedDateStr)
      setQuestions(response)
    } catch {
      setQuestions([])
    } finally {
      setIsLoadingQuestions(false)
    }
  }, [selectedDateStr])

  // メモを取得
  const fetchNotes = useCallback(async () => {
    setIsLoadingNotes(true)
    try {
      const data = await getTimelineNotes(selectedDateStr)
      // manual メモのみ表示（AI生成メモは除外）
      setNotes(data.filter((n) => !n.note_type || n.note_type === 'manual'))
    } catch {
      setNotes([])
    } finally {
      setIsLoadingNotes(false)
    }
  }, [selectedDateStr])

  // 日付変更時にリセット＆メモ取得
  useEffect(() => {
    setQuestions([])
    setActiveChat(null)
    fetchNotes()
  }, [selectedDateStr, fetchNotes])

  // ノート ローカル更新ヘルパー
  const handleNoteCreated = useCallback((note: TimelineNote) => {
    if (note.note_type && note.note_type !== 'manual') return
    setNotes((prev) => {
      // timestamp 昇順を維持して挿入（API も昇順で返す）
      const next = [...prev, note]
      next.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      return next
    })
  }, [])
  const handleNoteChanged = useCallback((note: TimelineNote) => {
    setNotes((prev) => prev.map((n) => (n.id === note.id ? note : n)))
  }, [])
  const handleNoteDeleted = useCallback((noteId: number) => {
    setNotes((prev) => prev.filter((n) => n.id !== noteId))
  }, [])

  // チャットモード
  if (activeChat) {
    return <ChatView question={activeChat} dateStr={selectedDateStr} onClose={() => setActiveChat(null)} />
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-5 overflow-y-auto">
      {/* メモ一覧 */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium flex items-center gap-1.5">
            <MessageSquare className="size-4 text-amber-500" />
            今日のメモ
          </h3>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setNoteSheetOpen(true)}>
            <Plus className="size-3 mr-1" />
            追加
          </Button>
        </div>
        {isLoadingNotes ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : notes.length > 0 ? (
          <div className="space-y-2">
            {notes.map((note) => (
              <TimelineNoteCard key={note.id} note={note} onChanged={handleNoteChanged} onDeleted={handleNoteDeleted} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">メモはまだありません</p>
        )}
      </section>

      {/* 日次サマリー */}
      <DailySummary dateStr={selectedDateStr} />

      {/* AI質問セクション */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium flex items-center gap-1.5">
            <Bot className="size-4 text-primary" />
            AIからの問いかけ
          </h3>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={fetchQuestions}
            disabled={isLoadingQuestions}
          >
            <RefreshCw className={`size-3 mr-1 ${isLoadingQuestions ? 'animate-spin' : ''}`} />
            {questions.length > 0 ? '更新' : '生成'}
          </Button>
        </div>

        {isLoadingQuestions ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : questions.length > 0 ? (
          <div className="space-y-2">
            {questions.map((q, i) => (
              <QuestionCard key={i} question={q} onSelect={setActiveChat} />
            ))}
          </div>
        ) : (
          <Card className="bg-muted/30">
            <CardContent className="p-4 text-center text-sm text-muted-foreground">
              「生成」を押すとAIがあなたの今日の行動をもとに質問を考えます
            </CardContent>
          </Card>
        )}
      </section>

      {/* 読書サマリー */}
      {currentHistory.length > 0 && (
        <section className="space-y-3">
          <Card className="bg-linear-to-br from-primary/10 to-primary/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <NotebookPen className="size-4" />
                今日の読書
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">読書時間</div>
                  <div className="font-medium">{formatMinutesJa(totalMinutes)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">セッション数</div>
                  <div className="font-medium">{currentHistory.length}回</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            {currentHistory.map((item) => (
              <ReadCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {/* メモ追加シート */}
      <NoteInputSheet
        open={noteSheetOpen}
        onOpenChange={setNoteSheetOpen}
        initialTimestamp={new Date(`${selectedDateStr}T${new Date().toTimeString().slice(0, 5)}`).toISOString()}
        onCreated={handleNoteCreated}
      />
    </div>
  )
}
